
import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHONE_PREFIXES } from "@/constants/phone";
import { getPhoneMaxLength } from "@/constants/phoneValidation";

interface PhoneNumberInputProps {
  selectedPrefix: string;
  phoneNumber: string;
  preferredMessaging: string;
  onPrefixChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
}

export const PhoneNumberInput = ({
  selectedPrefix,
  phoneNumber,
  preferredMessaging,
  onPrefixChange,
  onPhoneNumberChange,
}: PhoneNumberInputProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [maxLength, setMaxLength] = useState(10);

  useEffect(() => {
    setMaxLength(getPhoneMaxLength(selectedPrefix));
  }, [selectedPrefix]);

  const getPhoneNumberPlaceholder = () => {
    if (selectedPrefix === "+251") {
      return "951234567";
    }
    return "712345678";
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers in the phone field
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    
    if (sanitizedValue.length <= maxLength) {
      onPhoneNumberChange(sanitizedValue);
    }
  };

  const filteredPrefixes = searchQuery 
    ? PHONE_PREFIXES.filter(prefix => 
        prefix.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prefix.value.includes(searchQuery)
      )
    : PHONE_PREFIXES;

  return (
    <div className="space-y-2">
      <Label htmlFor="whatsappNumber">
        {preferredMessaging === "telegram" ? "Telegram Number" : "WhatsApp Number"}
      </Label>
      <div className="flex gap-2">
        <Select 
          value={selectedPrefix}
          onValueChange={onPrefixChange}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Select prefix" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto bg-white">
            <div className="px-2 py-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search country code..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <SelectGroup>
              <SelectLabel>Phone Prefixes</SelectLabel>
              {filteredPrefixes.map((prefix) => (
                <SelectItem key={prefix.value} value={prefix.value}>
                  {prefix.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <Input
          id="whatsappNumber"
          type="tel"
          inputMode="numeric"
          placeholder={getPhoneNumberPlaceholder()}
          className="flex-1 placeholder:text-gray-400"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          maxLength={maxLength}
        />
      </div>
      <p className="text-xs text-gray-500">
        Enter up to {maxLength} digits
      </p>
    </div>
  );
};
